export interface Grecaptcha {
  ready(callback: () => void): void
  render(
    container: HTMLElement,
    parameters: {
      sitekey: string
      size?: "invisible" | "compact" | "normal"
      callback?: (token: string) => void
      "error-callback"?: () => void
      "expired-callback"?: () => void
    }
  ): number
  execute(widgetId?: number): void
  reset(widgetId?: number): void
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha
  }
}

const SCRIPT_SRC = "https://www.google.com/recaptcha/api.js?render=explicit"
const SCRIPT_SELECTOR = 'script[src*="recaptcha/api.js"]'

let loadPromise: Promise<Grecaptcha> | null = null

export function loadGrecaptcha(): Promise<Grecaptcha> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("reCAPTCHA is not available"))
  }
  if (window.grecaptcha?.render) {
    return new Promise((resolve) => {
      window.grecaptcha!.ready(() => resolve(window.grecaptcha!))
    })
  }
  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (!window.grecaptcha) {
        loadPromise = null
        reject(new Error("reCAPTCHA failed to load"))
        return
      }
      window.grecaptcha.ready(() => resolve(window.grecaptcha!))
    }

    const existing = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR)
    if (existing) {
      if (window.grecaptcha) {
        finish()
        return
      }
      existing.addEventListener("load", finish, { once: true })
      existing.addEventListener(
        "error",
        () => {
          loadPromise = null
          reject(new Error("reCAPTCHA failed to load"))
        },
        { once: true }
      )
      return
    }

    const script = document.createElement("script")
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = finish
    script.onerror = () => {
      loadPromise = null
      reject(new Error("reCAPTCHA failed to load"))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}
